// src/pages/SportsPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SportsSidebar from '../components/sports/SportsSidebar';
import MatchCard from '../components/sports/MatchCard';
import { useBetSlip } from '../context/BetSlipContext';
import { FiFilter, FiGrid, FiList, FiMenu, FiRefreshCw, FiCalendar } from 'react-icons/fi';
import { FootballAPI } from '../services/footballApi';

const SportsPage = () => {
  const navigate = useNavigate();
  const [selectedSport, setSelectedSport] = useState('football');
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [availableDates, setAvailableDates] = useState([]);
  const [groupedMatches, setGroupedMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [oddsFormat, setOddsFormat] = useState('decimal');
  const [viewMode, setViewMode] = useState('grid');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [error, setError] = useState(null);
  const { betslipCount } = useBetSlip();

  // League priority function - top leagues first
  const getLeaguePriority = useCallback((leagueId, leagueName) => {
    const priorityMap = {
      1: 1,   // FIFA World Cup
      2: 2,   // UEFA Champions League
      39: 3,  // Premier League
      140: 4, // La Liga
      135: 5, // Serie A
      78: 6,  // Bundesliga
      61: 7,  // Ligue 1
    };
    
    if (priorityMap[leagueId]) {
      return priorityMap[leagueId];
    }
    
    const name = leagueName?.toLowerCase() || '';
    if (name.includes('fifa world cup') || name.includes('world cup')) return 1;
    if (name.includes('champions league')) return 2;
    if (name.includes('premier league')) return 3;
    if (name.includes('la liga')) return 4;
    if (name.includes('serie a')) return 5;
    if (name.includes('bundesliga')) return 6;
    if (name.includes('ligue 1')) return 7;
    if (name.includes('eredivisie')) return 8;
    if (name.includes('primeira liga')) return 9;
    if (name.includes('mls')) return 10;
    if (name.includes('usl championship')) return 11;
    if (name.includes('usl league one')) return 12;
    if (name.includes('usl league two')) return 13;
    
    return 999;
  }, []);

  // Fetch available dates
  const fetchAvailableDates = async () => {
    try {
      const response = await FootballAPI.getAvailableDates();
      if (response.success && response.data) {
        setAvailableDates(response.data);
      }
    } catch (error) {
      // console.error('Failed to fetch dates:', error);
    }
  };

  // Fetch football data
  const fetchFootballData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // console.log('Fetching fixtures for date:', selectedDate);
      
      const response = await FootballAPI.getFixtures(selectedDate);
      // console.log('API Response:', response);
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      if (response.data && response.data.length > 0) {
        let allGrouped = response.data;
        
        // Sort leagues by priority (top leagues first)
        allGrouped.sort((a, b) => {
          const priorityA = getLeaguePriority(a.league_id, a.league_name);
          const priorityB = getLeaguePriority(b.league_id, b.league_name);
          return priorityA - priorityB;
        });
        
        // Filter by league if selected
        if (selectedLeague) {
          allGrouped = allGrouped.filter(group => group.league_id === selectedLeague);
        }
        
        setGroupedMatches(allGrouped);
        setLastUpdate(new Date());
        // console.log('Set grouped matches:', allGrouped.length, 'leagues');
      } else {
        setGroupedMatches([]);
        setError('No football matches scheduled for this date');
      }
    } catch (error) {
      // console.error('Error fetching football data:', error);
      setError('Failed to load football matches. Please try again.');
      setGroupedMatches([]);
    } finally {
      setLoading(false);
    }
  };

  // Manual refresh handler
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchFootballData();
    setRefreshing(false);
  };

  // Date change handler
  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  // League select handler
  const handleLeagueSelect = (leagueId) => {
    setSelectedLeague(leagueId);
  };

  // Match click handler - navigate to match detail page
  const handleMatchClick = (matchId) => {
    navigate(`/match/${matchId}`);
  };

  // Initial load and auto-refresh
  useEffect(() => {
    if (selectedSport === 'football') {
      fetchFootballData();
      fetchAvailableDates();
      
      // Auto-refresh every 5 minutes
      const interval = setInterval(() => {
        if (selectedSport === 'football' && !loading) {
          fetchFootballData();
        }
      }, 300000);
      
      return () => clearInterval(interval);
    } else {
      setGroupedMatches([]);
      setLoading(false);
      if (selectedSport !== 'football') {
        setError(`${selectedSport.charAt(0).toUpperCase() + selectedSport.slice(1)} integration coming soon`);
      }
    }
  }, [selectedSport, selectedDate, selectedLeague]);

  // Generate date options (today + next 6 days)
  const getDateOptions = () => {
    const options = [];
    const today = new Date();
    
    for (let i = 0; i <= 6; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const dateStr = date.toISOString().split('T')[0];
      const displayStr = i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      options.push({ value: dateStr, label: displayStr });
    }
    
    return options;
  };

  const dateOptions = getDateOptions();

  // Flatten matches for stats
  const allMatches = groupedMatches.flatMap(group => group.fixtures);
  const liveMatches = allMatches.filter(m => ['1H', 'HT', '2H', 'ET', 'P'].includes(m.status));

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setIsFilterOpen(false);
  };

  const toggleFilterMenu = () => {
    setIsFilterOpen(!isFilterOpen);
    setIsMobileMenuOpen(false);
  };

  // Check if league is top tier
  const isTopLeague = (leagueId, leagueName) => {
    const priority = getLeaguePriority(leagueId, leagueName);
    return priority <= 7;
  };

  const isWorldCup = (leagueId, leagueName) => {
    return leagueId === 1 || leagueName?.toLowerCase().includes('world cup');
  };

  return (
    <div className="min-h-screen bg-deep-black pt-16">
      <Header />
      
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 gap-3">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white">Sports Betting</h1>
              {lastUpdate && (
                <p className="text-xs text-gray-400 mt-1">
                  Last updated: {lastUpdate.toLocaleTimeString()}
                </p>
              )}
            </div>
            
            <div className="flex sm:hidden items-center gap-2">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="p-2 bg-gray-800 rounded-lg"
              >
                <FiRefreshCw className={`w-5 h-5 text-white ${refreshing ? 'animate-spin' : ''}`} />
              </button>
              <button onClick={toggleMobileMenu} className="p-2 bg-gray-800 rounded-lg">
                <FiMenu className="w-5 h-5 text-white" />
              </button>
              <button onClick={toggleFilterMenu} className="p-2 bg-gray-800 rounded-lg">
                <FiFilter className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            {/* Date Picker */}
            <div className="relative">
              <select
                value={selectedDate}
                onChange={handleDateChange}
                className="bg-gray-800 text-white px-3 py-2 rounded-lg text-sm appearance-none cursor-pointer pr-8"
              >
                {dateOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <FiCalendar className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={14} />
            </div>

            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="hidden sm:flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white px-3 py-2 rounded-lg text-sm transition"
            >
              <FiRefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            
            <div className="hidden sm:flex items-center space-x-2 bg-gray-800 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-gray-700' : ''}`}
              >
                <FiGrid className="w-4 h-4 text-white" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-gray-700' : ''}`}
              >
                <FiList className="w-4 h-4 text-white" />
              </button>
            </div>
            
            <select 
              value={oddsFormat}
              onChange={(e) => setOddsFormat(e.target.value)}
              className="w-full sm:w-auto bg-gray-800 text-white px-3 sm:px-4 py-2 rounded-lg text-sm"
            >
              <option value="decimal">Decimal (2.0)</option>
              <option value="fractional">Fractional (1/1)</option>
              <option value="american">American (+100)</option>
            </select>
            
            <Link to="/betslip" className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white px-4 sm:px-6 py-2 rounded-lg font-semibold text-center text-sm sm:text-base">
              Betslip ({betslipCount})
            </Link>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 bg-red-900/50 border border-red-700 rounded-lg p-4">
            <p className="text-red-200 text-sm">{error}</p>
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-4">
          {/* Mobile Sidebar Overlay */}
          {isMobileMenuOpen && (
            <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40" onClick={toggleMobileMenu}>
              <div className="absolute left-0 top-0 h-full w-64 bg-gray-900 shadow-lg" onClick={(e) => e.stopPropagation()}>
                <div className="p-4">
                  <h3 className="text-lg font-bold text-white mb-4">Sports Categories</h3>
                  <SportsSidebar 
                    selectedSport={selectedSport}
                    onSportSelect={(sport) => {
                      setSelectedSport(sport);
                      setIsMobileMenuOpen(false);
                    }}
                    selectedDate={selectedDate}
                    onLeagueSelect={handleLeagueSelect}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Mobile Filter Overlay */}
          {isFilterOpen && (
            <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40" onClick={toggleFilterMenu}>
              <div className="absolute right-0 top-0 h-full w-64 bg-gray-900 shadow-lg p-4" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-lg font-bold text-white mb-4">Filter Options</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-gray-300 mb-2">Odds Format</label>
                    <select 
                      value={oddsFormat}
                      onChange={(e) => setOddsFormat(e.target.value)}
                      className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg"
                    >
                      <option value="decimal">Decimal (2.0)</option>
                      <option value="fractional">Fractional (1/1)</option>
                      <option value="american">American (+100)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-300 mb-2">View Mode</label>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setViewMode('grid')}
                        className={`flex-1 p-2 rounded ${viewMode === 'grid' ? 'bg-blue-600' : 'bg-gray-800'}`}
                      >
                        <FiGrid className="w-4 h-4 text-white mx-auto" />
                      </button>
                      <button
                        onClick={() => setViewMode('list')}
                        className={`flex-1 p-2 rounded ${viewMode === 'list' ? 'bg-blue-600' : 'bg-gray-800'}`}
                      >
                        <FiList className="w-4 h-4 text-white mx-auto" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Sidebar */}
          <div className="hidden lg:block w-full lg:w-64 flex-shrink-0">
            <SportsSidebar 
              selectedSport={selectedSport}
              onSportSelect={setSelectedSport}
              selectedDate={selectedDate}
              onLeagueSelect={handleLeagueSelect}
            />
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <div className="bg-gray-900 rounded-xl p-4 mb-4 sm:mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">
                    {selectedSport === 'football' ? 'Football Matches' : `${selectedSport.charAt(0).toUpperCase() + selectedSport.slice(1)}`}
                  </h2>
                  <p className="text-gray-400 text-sm">
                    {selectedSport === 'football' 
                      ? `Live and upcoming matches with real-time odds from Bet365`
                      : `Integration coming soon`}
                  </p>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <span className="hidden sm:inline">Showing</span>
                  <span className="bg-gray-800 px-3 py-1 rounded-lg">
                    {allMatches.length} matches in {groupedMatches.length} leagues
                  </span>
                  {liveMatches.length > 0 && (
                    <span className="bg-red-900/50 text-red-400 px-2 py-1 rounded-lg text-xs flex items-center gap-1">
                      <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></span>
                      {liveMatches.length} LIVE
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="lg:hidden flex items-center justify-between mb-4">
              <div className="text-sm text-gray-400">
                {allMatches.length} matches available
              </div>
              <div className="flex items-center space-x-2 bg-gray-800 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded ${viewMode === 'grid' ? 'bg-gray-700' : ''}`}
                >
                  <FiGrid className="w-4 h-4 text-white" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded ${viewMode === 'list' ? 'bg-gray-700' : ''}`}
                >
                  <FiList className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-8 sm:py-12">
                <div className="inline-block animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-t-2 border-b-2 border-blue-500"></div>
                <p className="text-gray-400 mt-4 text-sm sm:text-base">Loading matches...</p>
              </div>
            ) : groupedMatches.length === 0 ? (
              <div className="text-center py-8 sm:py-12">
                <div className="text-4xl sm:text-5xl mb-4">⚽</div>
                <h3 className="text-lg sm:text-xl font-bold text-white mb-2">No matches available</h3>
                <p className="text-gray-400 mb-6 text-sm sm:text-base">
                  {error || "No football matches scheduled for this date."}
                </p>
                <button
                  onClick={handleRefresh}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold"
                >
                  Refresh Data
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                {groupedMatches.map((group) => {
                  const isWorldCupLeague = isWorldCup(group.league_id, group.league_name);
                  const isTop = isTopLeague(group.league_id, group.league_name);
                  
                  return (
                    <div key={group.league_id} className="space-y-3">
                      {/* League Header with Priority Badge */}
                      <div className="flex items-center gap-2 bg-gray-800/50 rounded-lg p-3 sticky top-16 z-10 backdrop-blur-sm">
                        {group.league_logo && (
                          <img src={group.league_logo} alt={group.league_name} className="w-6 h-6 object-contain" />
                        )}
                        <h3 className="text-lg font-bold text-white">{group.league_name}</h3>
                        <span className="text-xs text-gray-400 bg-gray-700 px-2 py-1 rounded">
                          {group.fixtures.length} matches
                        </span>
                        
                      
                      </div>
                      
                      {/* Matches Grid */}
                      <div className={viewMode === 'grid' 
                        ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-3 sm:gap-4" 
                        : "space-y-3 sm:space-y-4"
                      }>
                        {group.fixtures.map(match => (
                          <div key={match.id} onClick={() => handleMatchClick(match.id)} className="cursor-pointer">
                            <MatchCard
                              match={match}
                              sport={selectedSport}
                              oddsFormat={oddsFormat}
                              viewMode={viewMode}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

          </div>
        </div>

        {/* Mobile Quick Navigation */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-gray-900 border-t border-gray-800 p-3 z-30">
          <div className="flex justify-around">
            <button onClick={toggleMobileMenu} className="flex flex-col items-center text-gray-400 hover:text-white">
              <FiMenu className="w-5 h-5 mb-1" />
              <span className="text-xs">Sports</span>
            </button>
            <Link to="/betslip" className="flex flex-col items-center text-gray-400 hover:text-white">
              <div className="relative">
                <span className="text-lg">📋</span>
                {betslipCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                    {betslipCount}
                  </span>
                )}
              </div>
              <span className="text-xs">Betslip</span>
            </Link>
            <button onClick={toggleFilterMenu} className="flex flex-col items-center text-gray-400 hover:text-white">
              <FiFilter className="w-5 h-5 mb-1" />
              <span className="text-xs">Filter</span>
            </button>
            <Link to="/live" className="flex flex-col items-center text-gray-400 hover:text-white">
              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse mb-1"></div>
              <span className="text-xs">Live</span>
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SportsPage;